/**
 * Datora Multi Discounts — cart integration.
 *
 * Drives the `co2` object provided by the "Datora | Multi Discounts" app embed.
 * co2.setDiscountCode() writes both Shopify's native cart `discount` field and the
 * `disc_cds` cart attribute in a single /cart/update.js call; the Datora discount
 * function reads `disc_cds` and generates the line discounts server-side.
 *
 * Scope: the cart. The discount display on the product price surfaces (PDP, cards, quick
 * view, search, recommendations) lives in 'assets/datora-prices.js', which picks up the
 * cartUpdate this file publishes.
 *
 * King specifics: the theme's cart classes in theme.js (CartItems, CartDrawerItems,
 * GiftWrapping) swap the cart DOM themselves and, as shipped, published nothing, so each of
 * them now publishes PUB_SUB_EVENTS.cartUpdate after its update. This file bridges that
 * event into the `cart.update` window event co2 listens for, and re-renders both cart
 * surfaces itself after a code change, since no theme code listens for cartUpdate.
 */

const datoraErrorKeys = {
  empty: 'errorEmpty',
  double: 'errorDouble',
  failed: 'errorFailed',
  invalid: 'errorInvalid'
};

const datoraTruncate = (str, len = 10) => (str.length > len ? str.substring(0, len) + '...' : str);

const applyDatoraDiscount = async function (trigger) {
  const container = trigger
    ? trigger.closest('.datora-discount')
    : document.querySelector('.datora-discount');
  if (!container) return;

  // The loading class only blocks the pointer; Enter in the focused field still gets here.
  if (container.classList.contains('loading')) return;

  const datoraInput = container.querySelector('input[name="datora-discount"]');
  if (!datoraInput) return;

  const discountCode = datoraInput.value.trim();
  if (discountCode.length < 1) {
    errorDatoraDiscount('show', 'empty');
    return;
  }

  // Submitting ends the draft; the field follows the cart from here on.
  if (typeof clearDatoraDraft === 'function') clearDatoraDraft();
  errorDatoraDiscount('hide');
  container.classList.add('loading');

  try {
    if (typeof co2 === 'undefined') {
      errorDatoraDiscount('show', 'failed', discountCode);
      return;
    }

    const currentCode = await co2.getDiscountCode();
    if (currentCode && currentCode.trim().toUpperCase() === discountCode.toUpperCase()) {
      errorDatoraDiscount('show', 'double', discountCode);
      return;
    }

    const applied = await co2.setDiscountCode(discountCode);
    if (applied) {
      refreshCartAndPrices();
      if (typeof restoreDiscountState === 'function') restoreDiscountState();
    } else {
      // The app rejected the code itself — that is "invalid", not a failure to reach it.
      errorDatoraDiscount('show', 'invalid', discountCode);
    }
  } catch (error) {
    // Either co2 call can reject. Without this the field keeps its loading state for good.
    console.error('[datora] applying the code failed:', error);
    errorDatoraDiscount('show', 'failed', discountCode);
  } finally {
    container.classList.remove('loading');
  }
};

const removeDatoraDiscount = async function (trigger) {
  const container = trigger
    ? trigger.closest('.datora-discount')
    : document.querySelector('.datora-discount');
  if (!container) return;
  if (container.classList.contains('loading')) return;

  if (typeof clearDatoraDraft === 'function') clearDatoraDraft();
  errorDatoraDiscount('hide');
  container.classList.add('loading');

  try {
    if (typeof co2 === 'undefined') return;

    const removed = await co2.removeDiscountCode();
    if (removed) {
      if (typeof clearActiveDiscountCode === 'function') clearActiveDiscountCode();
      refreshCartAndPrices();
    }
  } catch (error) {
    // Logged rather than shown: the code is still applied, and the field still says so.
    console.error('[datora] removing the code failed:', error);
  } finally {
    container.classList.remove('loading');
  }
};

function errorDatoraDiscount(type = 'show', reason = 'invalid', code = '') {
  const containers = document.querySelectorAll('.datora-discount');
  let needsRestore = false;

  containers.forEach((container) => {
    const errorDiv = container.querySelector('.datora-discount__error');

    if (type !== 'show') {
      container.classList.remove('error');
      if (errorDiv) errorDiv.classList.add('hidden');
      return;
    }

    const strings = (window.theme && theme.datoraStrings) || {};
    let errorAlert = strings[datoraErrorKeys[reason]];
    if (!errorAlert) return;
    errorAlert = errorAlert.replace('[[CODE]]', datoraTruncate(code.trim().toUpperCase(), 10));

    container.classList.add('error');
    if (errorDiv) {
      const target = errorDiv.querySelector('span');
      // textContent: the message carries the shopper's own code, and the strings are plain.
      if (target) target.textContent = errorAlert;
      errorDiv.classList.remove('hidden');
    }

    const input = container.querySelector('input[name="datora-discount"]');
    if (!input) return;

    if (reason === 'empty') return;

    // On a rejected code, restore the field to whatever is actually active.
    if (container.getAttribute('data-discount-state') === 'active') {
      needsRestore = true;
    } else {
      input.value = '';
    }
  });

  // Both outside the loop: restoreDiscountState() already walks every container, and focusing
  // per container would leave the caret in a hidden copy.
  if (needsRestore && typeof restoreDiscountState === 'function') restoreDiscountState();

  if (type === 'show' && reason === 'empty') {
    const visible = Array.from(containers).find((container) => container.offsetParent !== null);
    const input = (visible || containers[0])?.querySelector('input[name="datora-discount"]');
    if (input) input.focus();
  }
}

/**
 * The cart surfaces on the page, each with the section its markup is rendered from and the
 * way the theme itself swaps that markup in (mirrors CartDrawerItems / CartItems in theme.js).
 * The drawer is rendered by the header (settings.cart_behaviour == 'drawer'), never on the
 * cart page; the cart page is the main-cart section.
 */
function datoraCartSurfaces() {
  const surfaces = [];

  const drawer = document.querySelector('cart-drawer[cart-drawer-section]');
  if (drawer && drawer.dataset.id) {
    surfaces.push({ element: drawer, section: drawer.dataset.id, render: renderDatoraCartDrawer });
  }

  const page = document.getElementById('main-cart');
  if (page && page.dataset.id) {
    surfaces.push({ element: page, section: page.dataset.id, render: renderDatoraCartPage });
  }

  return surfaces;
}

function datoraSectionElement(sectionHtml, selector) {
  return new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector(selector);
}

/**
 * Header (shipping bar), body (lines) and footer content (discount field, totals, checkout)
 * are swapped separately, as CartDrawerItems.updateQuantity() does, so the drawer's note and
 * shipping toggles keep their listeners. A drawer whose shape changed (it emptied or filled)
 * is taken over wholesale, as CartDrawer.renderContents() does after an add to cart.
 */
function renderDatoraCartDrawer(drawer, sectionHtml) {
  const source = datoraSectionElement(sectionHtml, '.side-drawer');
  if (!source) return;

  const regions = ['[data-cart-drawer-header]', '[data-cart-drawer-body]', '[data-cart-drawer-content]'];
  const pairs = regions.map((selector) => [drawer.querySelector(selector), source.querySelector(selector)]);

  if (pairs.every(([target, replacement]) => target && replacement)) {
    pairs.forEach(([target, replacement]) => {
      target.innerHTML = replacement.innerHTML;
    });
    return;
  }

  drawer.innerHTML = source.innerHTML;
  if (typeof drawer.setUpCartButtonListeners === 'function') drawer.setUpCartButtonListeners();
}

function renderDatoraCartPage(page, sectionHtml) {
  const source = datoraSectionElement(sectionHtml, '.main--cart-render');
  if (!source) return;
  page.innerHTML = source.innerHTML;
}

/**
 * Re-render the cart. The discount is already applied server-side by the Datora
 * function, so the refreshed Liquid carries the discounted line prices and totals.
 * A `sections`-only POST to cart/update.js mutates nothing but returns the cart plus
 * the rendered sections.
 */
let datoraRefreshController = null;

function refreshCartAndPrices() {
  const surfaces = datoraCartSurfaces();
  const body = JSON.stringify({
    sections: surfaces.map((surface) => surface.section),
    sections_url: window.location.pathname
  });

  // A quick apply then remove would otherwise let the older response land last and repaint
  // the cart with the previous state.
  if (datoraRefreshController) datoraRefreshController.abort();
  const controller = new AbortController();
  datoraRefreshController = controller;

  fetch(routes.cart_update_url, { ...fetchConfig('json'), ...{ body }, signal: controller.signal })
    .then((response) => {
      if (!response.ok) throw new Error('cart update failed: ' + response.status);
      return response.json();
    })
    .then((parsedState) => {
      if (datoraRefreshController !== controller) return;

      surfaces.forEach((surface) => {
        const sectionHtml = parsedState.sections && parsedState.sections[surface.section];
        if (sectionHtml) surface.render(surface.element, sectionHtml);
      });

      // The free-shipping bar reads its width from the swapped-in markup; same delay the theme uses.
      if (surfaces.length && typeof shippingconversion === 'function') setTimeout(shippingconversion, 400);

      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'datora', cartData: parsedState });
    })
    .catch((e) => {
      if (e.name === 'AbortError') return;
      console.error('[datora] cart refresh error:', e);
    })
    .finally(() => {
      if (datoraRefreshController === controller) datoraRefreshController = null;
    });
}

/**
 * The cart object a cartUpdate carries, if it is one. ProductForm publishes the /cart/add.js
 * response, a line item; co2 re-reads /cart.js itself when handed anything but a cart.
 */
function datoraCartFromEvent(event) {
  const cart = event && (event.cartData || event.cart);
  return cart && Array.isArray(cart.items) && typeof cart.total_price === 'number' ? cart : null;
}

// Bridge the theme's pub/sub into the event co2 listens for, so its internal cart
// state stays in sync with every cart mutation the theme performs.
subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
  // Skip our own refresh: co2 already holds current state (it performed the update, and
  // the sections-only POST mutates nothing). Re-notifying it here can loop back through
  // co2.afterCartAttributeChange into another refresh.
  if (!event || event.source !== 'datora') {
    // The theme has just rendered a newer cart (quantity change, remove, gift wrap, add). A
    // Datora refresh still in flight was requested before that and would paint the older
    // cart over it when it lands — drop it. The theme's render already carries the code state.
    if (datoraRefreshController) {
      datoraRefreshController.abort();
      datoraRefreshController = null;
    }
    window.dispatchEvent(new CustomEvent('cart.update', { detail: { cart: datoraCartFromEvent(event) } }));
  }
  // The entry snippet renders its own state from cart.attributes.disc_cds, so a
  // re-render is already correct. This only covers the case where co2's state changed
  // without a re-render — deferred a frame so it lands after any DOM swap other
  // cartUpdate subscribers perform, rather than on nodes about to be replaced.
  requestAnimationFrame(function () {
    if (typeof restoreDiscountState === 'function') restoreDiscountState();
  });
});

// co2 rewrites the cart attribute when a code arrives via link/cookie — re-render then.
window.addEventListener('co2.afterCartAttributeChange', function () {
  refreshCartAndPrices();
});

window.addEventListener('co2.cart.initialized', function () {
  if (typeof restoreDiscountState === 'function') restoreDiscountState();
});

// Hide the field on a page without the app; restoreDiscountState() reveals it again on any
// later sign of life (see syncDatoraFieldAvailability in component-datora-discount-entry.js).
window.addEventListener('load', function () {
  if (typeof syncDatoraFieldAvailability === 'function') syncDatoraFieldAvailability();
});
